import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ChannelConfig, ChannelMeta, ChannelStatus } from '../../types/messaging.js';
import * as messagingApi from '../../services/messaging-api.js';

@customElement('cp-channel-card')
export class CpChannelCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) meta!: ChannelMeta;
  @property({ type: Object }) config: ChannelConfig | null = null;
  @property({ type: String }) companyLabel: string | null = null;
  @property({ type: String }) agentLabel: string | null = null;

  @state() private _expanded = false;
  @state() private _credentials = '';
  @state() private _displayName = '';
  @state() private _saving = false;
  @state() private _testing = false;
  @state() private _deleting = false;
  @state() private _error = '';
  @state() private _successMessage = '';
  @state() private _showCredentials = false;
  @state() private _qrCode = '';

  private _getStatusLabel(status: ChannelStatus): string {
    switch (status) {
      case 'connected': return 'Verbunden';
      case 'connecting': return 'Verbindet...';
      case 'disconnected': return 'Getrennt';
      case 'error': return 'Fehler';
    }
  }

  private _getStatusClass(status: ChannelStatus): string {
    switch (status) {
      case 'connected': return 'cp-channel-card__status--connected';
      case 'connecting': return 'cp-channel-card__status--connecting';
      case 'disconnected': return 'cp-channel-card__status--disconnected';
      case 'error': return 'cp-channel-card__status--error';
    }
  }

  private _toggleExpanded(): void {
    this._expanded = !this._expanded;
    this._error = '';
    this._successMessage = '';
    if (this._expanded && this.config) {
      this._displayName = this.config.display_name;
      this._credentials = '';
    } else if (this._expanded) {
      this._displayName = this.meta.label;
      this._credentials = '';
    }
  }

  private _onCredentialsInput(e: Event): void {
    this._credentials = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onDisplayNameInput(e: Event): void {
    this._displayName = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private async _save(): Promise<void> {
    if (this._saving) return;

    this._saving = true;
    this._error = '';
    this._successMessage = '';

    try {
      if (this.config) {
        const updateData: Record<string, string> = {};
        if (this._displayName.trim()) updateData.display_name = this._displayName.trim();
        if (this._credentials.trim()) updateData.credentials = this._credentials.trim();
        const updated = await messagingApi.updateChannel(this.config.id, updateData);
        this._fireConfigChanged(updated);
        this._credentials = '';
        this._successMessage = 'Konfiguration aktualisiert';
      } else {
        if (!this._credentials.trim()) {
          this._error = `${this.meta.credentialLabel} ist erforderlich`;
          return;
        }
        const created = await messagingApi.createChannel({
          channel_type: this.meta.type,
          display_name: this._displayName.trim() || this.meta.label,
          credentials: this._credentials.trim(),
        });
        this._fireConfigChanged(created);
        this._credentials = '';
        this._successMessage = 'Channel konfiguriert';
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._saving = false;
    }
  }

  private async _test(): Promise<void> {
    if (this._testing || !this.config) return;

    this._testing = true;
    this._error = '';
    this._successMessage = '';

    try {
      const result = await messagingApi.testChannel(this.config.id);
      if (result.success) {
        this._successMessage = 'Verbindung erfolgreich';
        this._fireConfigChanged({ ...this.config, status: result.status ?? 'connected' });
      } else {
        this._error = result.message ?? 'Verbindungstest fehlgeschlagen';
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._testing = false;
    }
  }

  private async _delete(): Promise<void> {
    if (this._deleting || !this.config) return;

    this._deleting = true;
    this._error = '';

    try {
      await messagingApi.deleteChannel(this.config.id);
      this._fireConfigChanged(null);
      this._expanded = false;
      this._successMessage = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._deleting = false;
    }
  }

  private _fireConfigChanged(config: ChannelConfig | null): void {
    this.dispatchEvent(new CustomEvent('config-changed', {
      detail: { channelType: this.meta.type, config },
      bubbles: true,
      composed: true,
    }));
  }

  updateStatus(status: ChannelStatus, errorMessage?: string): void {
    if (this.config) {
      this._fireConfigChanged({
        ...this.config,
        status,
        error_message: errorMessage ?? this.config.error_message,
      });
    }
  }

  setQrCode(qrCode: string): void {
    this._qrCode = qrCode;
  }

  private _renderQrCode() {
    if (!this._qrCode || !this.meta.usesQrCode) return nothing;
    return html`
      <div class="cp-channel-card__qr">
        <p class="cp-channel-card__qr-label">QR-Code scannen (in WhatsApp anzeigen):</p>
        <pre class="cp-channel-card__qr-text">${this._qrCode}</pre>
      </div>
    `;
  }

  render() {
    const status: ChannelStatus = this.config?.status ?? 'disconnected';
    const isConfigured = !!this.config;

    return html`
      <div class="cp-channel-card ${this._expanded ? 'cp-channel-card--expanded' : ''}">
        <div class="cp-channel-card__header" @click=${this._toggleExpanded}>
          <span class="cp-channel-card__icon">${this.meta.icon}</span>
          <span class="cp-channel-card__name">
            ${this.config?.display_name ?? this.meta.label}
            ${this.companyLabel ? html`<span class="cp-channel-card__company"> — ${this.companyLabel}</span>` : nothing}
            ${this.agentLabel ? html`<span class="cp-channel-card__company"> — ${this.agentLabel}</span>` : nothing}
          </span>
          <span class="cp-channel-card__status ${this._getStatusClass(status)}">
            ${this._getStatusLabel(status)}
          </span>
          <span class="cp-channel-card__chevron">${this._expanded ? '▲' : '▼'}</span>
        </div>

        ${this._expanded ? html`
          <div class="cp-channel-card__body">
            ${this._error ? html`
              <div class="cp-channel-card__error">${this._error}</div>
            ` : nothing}

            ${this._successMessage ? html`
              <div class="cp-channel-card__success">${this._successMessage}</div>
            ` : nothing}

            ${this.config?.error_message ? html`
              <div class="cp-channel-card__error">${this.config.error_message}</div>
            ` : nothing}

            ${this._renderQrCode()}

            <div class="cp-channel-card__form">
              <div class="cp-channel-card__field">
                <label class="cp-channel-card__label">Anzeigename</label>
                <input
                  class="cp-channel-card__input"
                  type="text"
                  placeholder=${this.meta.label}
                  .value=${this._displayName}
                  @input=${this._onDisplayNameInput}
                />
              </div>

              <div class="cp-channel-card__field">
                <label class="cp-channel-card__label">${this.meta.credentialLabel}</label>
                <div class="cp-channel-card__input-group">
                  <input
                    class="cp-channel-card__input cp-channel-card__input--mono"
                    .type=${this._showCredentials ? 'text' : 'password'}
                    placeholder=${isConfigured ? 'Bereits konfiguriert - leer lassen um beizubehalten' : this.meta.credentialPlaceholder}
                    .value=${this._credentials}
                    @input=${this._onCredentialsInput}
                  />
                  <button
                    class="cp-channel-card__btn cp-channel-card__btn--icon"
                    @click=${() => { this._showCredentials = !this._showCredentials; }}
                    title=${this._showCredentials ? 'Verbergen' : 'Anzeigen'}
                  >${this._showCredentials ? '🙈' : '👁'}</button>
                </div>
                <span class="cp-channel-card__hint">${this.meta.credentialHint}</span>
              </div>

              <div class="cp-channel-card__actions">
                <button
                  class="cp-channel-card__btn cp-channel-card__btn--save"
                  @click=${this._save}
                  ?disabled=${this._saving}
                >${this._saving ? 'Speichert...' : 'Speichern'}</button>

                ${isConfigured ? html`
                  <button
                    class="cp-channel-card__btn cp-channel-card__btn--test"
                    @click=${this._test}
                    ?disabled=${this._testing}
                  >${this._testing ? 'Testet...' : 'Verbindung testen'}</button>

                  <button
                    class="cp-channel-card__btn cp-channel-card__btn--delete"
                    @click=${this._delete}
                    ?disabled=${this._deleting}
                  >${this._deleting ? 'Loescht...' : 'Entfernen'}</button>
                ` : nothing}
              </div>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-channel-card': CpChannelCard;
  }
}
